import { W } from "./day-styles";
import type { AreaSport } from "./graph-types";
import type { PlannerPreferences } from "./preferences";
import {
  MAX_ROUTE_OUTCOMES, MAX_ROUTE_OUTCOME_BYTES, ROUTE_OUTCOME_RETENTION_MS,
  parseRouteOutcome, readRouteOutcomes, type RouteOutcomeReason, type RouteOutcomeRecord,
} from "./route-outcomes";

const KEY = "peakwave:personal-route-preferences:v1";
export const PERSONAL_PREFERENCES_CHANGED = "peakwave-personal-preferences-changed";
export const MAX_PERSONAL_PREFERENCE_BYTES = 2048;
export const MIN_PERSONAL_FEEDBACK_DAYS = 3;
const MIN_SUPPORT_DAYS = 2;
type SportSetting = { enabled: boolean; resetAt: string | null };
export type PersonalPreferenceSettings = { version: 1; ski: SportSetting; mtb: SportSetting };
const emptySettings = (): PersonalPreferenceSettings => ({ version: 1, ski: { enabled: false, resetAt: null }, mtb: { enabled: false, resetAt: null } });
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);

function parseSetting(value: unknown): SportSetting | null {
  if (!object(value) || typeof value.enabled !== "boolean" || !(value.resetAt === null ||
    (typeof value.resetAt === "string" && value.resetAt.length <= 32 && Number.isFinite(Date.parse(value.resetAt))))) return null;
  return { enabled: value.enabled, resetAt: value.resetAt };
}

export function readPersonalPreferenceSettings(): PersonalPreferenceSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw || new TextEncoder().encode(raw).length > MAX_PERSONAL_PREFERENCE_BYTES) return emptySettings();
    const value: unknown = JSON.parse(raw);
    if (!object(value) || value.version !== 1) return emptySettings();
    const ski = parseSetting(value.ski), mtb = parseSetting(value.mtb);
    return ski && mtb ? { version: 1, ski, mtb } : emptySettings();
  } catch { return emptySettings(); }
}

/** Only the explicit device choice is stored here. Applied settings use the
 * existing area preference store; no user model, identity or score weights. */
export function setPersonalPreferenceLearning(sport: AreaSport, action: "enable" | "disable" | "reset", now = new Date()): boolean {
  try {
    const settings = readPersonalPreferenceSettings();
    settings[sport] = action === "reset"
      ? { ...settings[sport], resetAt: now.toISOString() }
      : { ...settings[sport], enabled: action === "enable" };
    localStorage.setItem(KEY, JSON.stringify(settings));
    if (typeof window !== "undefined") window.dispatchEvent(new Event(PERSONAL_PREFERENCES_CHANGED));
    return true;
  } catch { return false; }
}

export type PersonalSuggestionId = "flow" | "variety" | "quiet" | "shorter";
export type PersonalSuggestion = { id: PersonalSuggestionId; title: string; explanation: string; supportingDays: number; reason: RouteOutcomeReason };
export type PersonalPreferenceReview = { sport: AreaSport; feedbackCount: number; dayCount: number; suggestions: PersonalSuggestion[] };

export const PERSONAL_PREFERENCE_SIGNALS: readonly { id: PersonalSuggestionId; title: string; explanation: string; reason: RouteOutcomeReason; positive?: boolean }[] = [
  { id: "flow", title: "Prefer flowing runs", explanation: "Turn on the existing flowing-runs preference.", reason: "great-flow", positive: true },
  { id: "variety", title: "Explore more terrain", explanation: "Turn on the existing terrain-variety preference.", reason: "good-variety", positive: true },
  { id: "quiet", title: "Prefer quieter routes", explanation: "Turn on the existing quieter-routes preference. Queues remain estimates.", reason: "too-much-waiting" },
  { id: "shorter", title: "Aim for a shorter route", explanation: "Set the route distance target to Half day. Your start, finish point and finish deadline stay the same.", reason: "day-too-long" },
];

/** Count explicit feedback days, not repeated revisions or replacement routes.
 * These are transparent suggestion rules, not fitted enjoyment predictions. */
export function reviewPersonalPreferences(raw: readonly unknown[], sport: AreaSport, resetAt: string | null = null, now = new Date()): PersonalPreferenceReview {
  const empty: PersonalPreferenceReview = { sport, feedbackCount: 0, dayCount: 0, suggestions: [] };
  if (raw.length > MAX_ROUTE_OUTCOMES) return empty;
  try { if (new TextEncoder().encode(JSON.stringify(raw)).length > MAX_ROUTE_OUTCOME_BYTES) return empty; } catch { return empty; }
  const latest = new Map<string, RouteOutcomeRecord>();
  const reset = resetAt === null ? -Infinity : Date.parse(resetAt);
  if (Number.isNaN(reset)) return empty;
  for (const value of raw) {
    const record = parseRouteOutcome(value);
    if (!record || record.sport !== sport) continue;
    const reported = Date.parse(record.reportedAt);
    if (reported < now.getTime() - ROUTE_OUTCOME_RETENTION_MS || reported > now.getTime() + 300_000 || reported <= reset
      || Date.parse(record.routeDate) < now.getTime() - ROUTE_OUTCOME_RETENTION_MS || Date.parse(record.routeDate) > now.getTime() + 86_400_000) continue;
    const previous = latest.get(record.journeyId);
    if (!previous || record.revision > previous.revision || (record.revision === previous.revision && record.reportedAt > previous.reportedAt)) latest.set(record.journeyId, record);
  }
  const records = [...latest.values()].filter(record => record.journeyScope === "selected-route");
  const dayCount = new Set(records.map(record => record.routeDate)).size;
  const suggestions = dayCount < MIN_PERSONAL_FEEDBACK_DAYS ? [] : PERSONAL_PREFERENCE_SIGNALS.flatMap(signal => {
    const supportingDays = new Set(records.filter(record => record.reasons.includes(signal.reason) && (!signal.positive || record.wouldRideAgain === "yes")).map(record => record.routeDate)).size;
    return supportingDays >= MIN_SUPPORT_DAYS ? [{ ...signal, supportingDays }] : [];
  });
  return { sport, feedbackCount: records.length, dayCount, suggestions };
}

/** The allowlist is deliberately small. Even a forged selection cannot change
 * ability, terrain ceilings, lift access, effort budgets, meeting or times. */
export function applyPersonalPreferenceSuggestions(prefs: PlannerPreferences, review: PersonalPreferenceReview, selected: readonly PersonalSuggestionId[]): { preferences: PlannerPreferences; touchedFields: string[]; changes: string[] } {
  let preferences = prefs;
  const touchedFields: string[] = [], changes: string[] = [];
  if (prefs.sport !== review.sport) return { preferences, touchedFields, changes };
  for (const id of new Set(selected)) {
    const suggestion = review.suggestions.find(item => item.id === id);
    if (!suggestion) continue;
    if (id === "shorter") {
      if (preferences.distance === "short" || preferences.distance === "half-day") continue;
      preferences = { ...preferences, distance: "half-day" };
      touchedFields.push("distance"); changes.push("Route distance target: Half day");
    } else {
      const objective = id === "flow" ? "cruise" : id === "variety" ? "coverage" : id === "quiet" ? "quiet" : null;
      if (!objective || preferences.objectives[objective] > 0) continue;
      preferences = { ...preferences, objectives: { ...preferences.objectives, [objective]: W } };
      touchedFields.push(`objectives.${objective}`); changes.push(suggestion.title);
    }
  }
  return { preferences, touchedFields, changes };
}

/** A stable string snapshot works with useSyncExternalStore without duplicating
 * the feedback database. Every read retains the existing count/age/byte bounds. */
export function personalPreferenceSnapshot(): string {
  return JSON.stringify({ settings: readPersonalPreferenceSettings(), records: readRouteOutcomes() });
}
